import React, { useState } from 'react';
import { Mail, Shield, RefreshCw, Lock } from 'lucide-react';
export default function EmailVerificationPage({ email, setPage, mode }) {
  const [isResetMode, setIsResetMode] = useState(mode === 'reset'); // ← accept "reset" mode from App.js

  const [otp, setOtp] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpVerifiedForReset, setOtpVerifiedForReset] = useState(false); // after verifying OTP for reset

  const handleVerify = async () => {
    setLoading(true);
    setMessage('');
    setStatus('');
    try {
      const response = await fetch('http://kngservice.revampapps.com/VerifiedCustomer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ Mail: email, Otp: otp })
      });

      const data = await response.json();
      const result = data?.VerifiedCustomerjs?.Table?.[0];

      if (result?.Status === 'Success') {
        if (isResetMode) {
          setOtpVerifiedForReset(true);
          setStatus('success');
          setMessage('OTP verified. Please enter new password.');
        } else {
          setStatus('success');
          setMessage(result.Message || 'Email verified successfully.');
          setTimeout(() => {
            setPage('login');
          }, 2000);
        }
      } else {
        setStatus('error');
        setMessage(result?.Message || 'Invalid OTP or Email.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('An error occurred. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setMessage('');
    setStatus('');
    try {
      const response = await fetch(`http://kngservice.revampapps.com/ResendOtp?Mail=${encodeURIComponent(email)}`);
      const data = await response.json();
      const result = data?.ResendOtpjs?.Table?.[0];

      if (result?.Status === 'Success') {
        setStatus('info');
        setMessage('OTP resent. Please check your email.');
      } else {
        setStatus('error');
        setMessage(result?.Message || 'Failed to resend OTP.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('An error occurred while resending OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(
        `http://kngservice.revampapps.com/ResetPwd?Mail=${encodeURIComponent(email)}&Otp=${otp}&NewPwd=${encodeURIComponent(newPwd)}`
      );
      const data = await response.json();
      const result = data?.ResetPwdjs?.Table?.[0];

      if (result?.Status === 'Success') {
        setStatus('success');
        setMessage(result.Message || 'Password reset successfully.');
        setTimeout(() => {
          setPage('login');
        }, 2000);
      } else {
        setStatus('error');
        setMessage(result?.Message || 'Failed to reset password.');
      }
    } catch (err) {
      console.error(err);
      setStatus('error');
      setMessage('Password reset failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const triggerForgotPasswordFlow = () => {
    setIsResetMode(true);
    setOtp('');
    setNewPwd('');
    setOtpVerifiedForReset(false);
    handleResendOtp(); // send OTP
  };

  return (
    <div className="container mx-auto max-w-xl text-center py-12 px-4">
      <div className="bg-white p-10 rounded-2xl shadow-xl inline-block w-full">
        <Mail className="text-teal-500 h-16 w-16 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {isResetMode ? 'Reset Your Password' : 'Verify Your Email'}
        </h2>
        <p className="text-gray-600 mb-6">
          A verification code has been sent to <strong>{email}</strong>.
        </p>

        {/* OTP Input */}
        {!otpVerifiedForReset && (
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Shield className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              maxLength={6}
              className="w-full px-3 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:ring-teal-500 focus:border-teal-500"
            />
          </div>
        )}

        {/* Status Message */}
        {message && (
          <div
            className={`text-sm p-3 rounded mb-4 ${
              status === 'success'
                ? 'text-green-700 bg-green-100'
                : status === 'info'
                ? 'text-blue-700 bg-blue-100'
                : 'text-red-700 bg-red-100'
            }`}
          >
            {message}
          </div>
        )}

        {/* Actions */}
        {!isResetMode || !otpVerifiedForReset ? (
          <button
            onClick={handleVerify}
            disabled={loading || otp.length === 0}
            className={`w-full font-bold py-3 rounded-lg transition shadow ${
              loading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-teal-600 text-white hover:bg-teal-700'
            }`}
          >
            {loading ? 'Verifying...' : isResetMode ? 'Verify OTP for Reset' : 'Verify Email'}
          </button>
        ) : (
          <>
            {/* New Password Input */}
            <div className="mt-6 relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Enter New Password"
                className="w-full px-3 py-3 pl-12 border border-gray-300 rounded-lg shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <button
              onClick={handleResetPassword}
              disabled={loading || !newPwd}
              className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg shadow"
            >
              Reset Password
            </button>
          </>
        )}

        {/* Resend OTP + Forgot Password Buttons */}
        {!otpVerifiedForReset && (
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={handleResendOtp}
              className="flex items-center justify-center gap-2 text-sm text-teal-700 font-medium hover:underline"
            >
              <RefreshCw className="h-4 w-4" />
              Resend OTP
            </button>

            <button
              onClick={triggerForgotPasswordFlow}
              className="flex items-center justify-center gap-2 text-sm text-gray-700 font-medium hover:underline"
            >
              <Lock className="h-4 w-4" />
              Forgot Password
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
